import { Card, useTheme } from "@mui/material";
import DescriptionIcon from '@mui/icons-material/Description';
import LoopIcon from '@mui/icons-material/Loop';
import { AmalgamWasteItem, WasteStatus } from "../types";
import CompliancePlip from "../../../../components/CompliancePlip";
import { amalgam2 } from "../../../../icons";
import { standardDateFormat } from "../../../../helpers/date";
import { TableButton } from "../../../../components/Buttons.styled";
import { useState } from "react";
import axios from "axios";
import constants from "../../../../constants";
import { useAppDispatch } from "../../../../redux/store";
import ManifestModal from "../components/ManifestModal";
import ActiveInactiveModal from "../components/ActiveInactiveModal";
import { itemImages } from "../../../../helpers/image";
import { addError, addSuccess } from "../../../../redux/alerts";
import useCustomer from "../../../../hooks/useCustomer";

type Props = {
  amalgam: AmalgamWasteItem,
  getWasteData: () => Promise<void>,
}

const WasteItem: React.FC<Props> = ({ amalgam, getWasteData }) => {
  const theme = useTheme();
  const dispatch = useAppDispatch();

  const customer = useCustomer()

  const itemStatus = amalgam.used_date === null ? 'inUse' : 'used';

  const [loadingItemId, setLoadingItemId] = useState<number | null>(null);
  const [openManifest, setOpenManifest] = useState<boolean>(false);
  const [openActiveUsed, setOpenActiveUsed] = useState<boolean>(false);

  let processor = {
    name: 'Pristine Environmental',
    street: '3511 132nd St SW',
    city: 'Lynnwood',
    state: 'WA',
    zip: '98087',
    facilityPermit: 'PHWH.FX.60413153'
  };

  const getStatus = (status: number | undefined) => {
    if (status === WasteStatus.inUse) {
      return 'In Use';
    } else if (status === WasteStatus.used) {
      return 'Used';
    } else if (status === WasteStatus.destroyed) {
      return 'Processed';
    }
    return 'Unknown'
  }

  const getImage = (image: string | undefined) => {
    if (image) {
      const itemImage = itemImages[`${image}.png`];
      return itemImage
    }
    return amalgam2;
  }

  const handleMarkAsUsedActive = async (usedDate?: string | null) => {
    setOpenActiveUsed(false);

    try {
      const itemDetails = {
        customer_number: amalgam.customer_number,
        status: amalgam.status,
        active: amalgam.active,
        item_number: amalgam.item_number,
        container_id: amalgam.container_id,
        used_date: amalgam.disposal_status === WasteStatus.used ? null : usedDate ?? String(new Date()),
        serial_number: amalgam.serial_number,
        shipped_date: amalgam.shipped_date
      }

      setLoadingItemId(amalgam.container_id)

      await axios.put(`${constants.ONTRAQ_API_URL}/api2/${amalgam.customer_number}/amalgam`, { ...itemDetails })
      await getWasteData();

      dispatch(addSuccess(amalgam.disposal_status === WasteStatus.used ? 'The item has been marked as In Use' : 'The item has been marked as Used'));
      setLoadingItemId(null);
    } catch (error) {
      setLoadingItemId(null)
      console.error(error);
      const message = error instanceof Error ? error.message : 'There was an error updating the item';
      dispatch(addError(message));
    }
  }

  const handleClose = () => {
    setOpenManifest(false);
  }

  const handleOpen = () => {
    setOpenManifest(true);
  }

  return (
    <Card sx={{ p: 2, mb: 2, color: theme.palette.color50555b.main, display: 'flex', flex: 'space-between' }} variant="outlined">
      <div className="flex flex-align-start flex-1">
        <img src={getImage(amalgam?.item?.images)} alt="kit" style={{ width: '50px' }} className="m-r-12" />
        <div>
          <div className="flex flex-align-center">
            <CompliancePlip medium={true} status={amalgam.status} message={amalgam?.amalgam_compliance?.compliance?.message_short} className="m-r-12" />
            <span className="f-s-17 m-r-8">{amalgam?.item?.name ?? 'Amalgam Waste Container'}</span>
            <LoopIcon sx={{ color: amalgam?.isRenewal ? theme.palette.success.main : theme.palette.inactive.main }} fontSize="small" />
          </div>
          <div className="f-s-13">
            <div>Items #: {amalgam.item_number}</div>
            <div>Product Serial #: {amalgam.serial_number}</div>
            {amalgam.used_date && <div>Used: {standardDateFormat(amalgam.used_date)}</div>}
            {amalgam.received_date && <div>Shipped: {standardDateFormat(amalgam.received_date)}</div>}
            {amalgam.received_date && <div>Received: {standardDateFormat(amalgam.received_date)}</div>}
            {amalgam.processed_date && <div>Processed: {standardDateFormat(amalgam.processed_date)}</div>}
            <div>Status: {getStatus(amalgam.disposal_status)}</div>
            {amalgam.disposal_status === WasteStatus.destroyed && (
              <div className="flex link pointer" onClick={handleOpen}>
                <DescriptionIcon fontSize="small" />
                <div>Processing Certificate</div>
              </div>
            )}
          </div>
        </div>
      </div>
      {(amalgam.disposal_status === WasteStatus.inUse || amalgam.disposal_status === WasteStatus.used) && (
        <div>
          <TableButton variant="contained" onClick={() => setOpenActiveUsed(true)} disabled={loadingItemId === amalgam.container_id}>
            Mark as {amalgam.disposal_status === WasteStatus.inUse ? 'Used' : 'Active'}
          </TableButton>
        </div>
      )}
      <ManifestModal open={openManifest} handleClose={handleClose} category="amalgamWaste" customer={customer} item={amalgam} processor={processor} />
      <ActiveInactiveModal itemStatus={itemStatus} open={openActiveUsed} handleClose={() => setOpenActiveUsed(false)} handleSubmit={handleMarkAsUsedActive} />
    </Card>
  )
}

export default WasteItem;